import isEqual from 'lodash/isEqual';

import {appendFieldError, createDropdown, createInputField} from '../forms/formFields';
import {cloneValue} from '../forms/formState';

const embeddingProviders = [
    'AmazonBedrockCohere', 'AmazonBedrockTitan', 'AzureOpenAI', 'GoogleGemini', 'MistralAI', 'Ollama', 'OpenAI'
];

const embeddingModelValue = value => ({
    credentials: value?.credentials || '',
    provider: value?.provider || '',
    model: value?.model || '',
    dimensions: value?.dimensions ?? null,
    ...(value?.endpoint ? {endpoint: value.endpoint} : {})
});

const managedEmbeddingModelValue = value => {
    const model = {...embeddingModelValue(value), dimensions: 1536};
    if (model.provider !== 'AzureOpenAI') {
        delete model.endpoint;
    }
    return model;
};

const stringValue = value => value === null || typeof value === 'undefined' ? '' : String(value);

const listedProviders = new Set(['AmazonBedrockCohere', 'AmazonBedrockTitan', 'GoogleGemini', 'OpenAI']);

const createEmbeddingModelFields = ({model, errors = {}, mxResources, onChange, onRender = () => {},
                                    managed = false}) => {
    const fields = document.createElement('div');
    fields.className = 'ui form embedding-model-fields';
    let current = managed ? managedEmbeddingModelValue(model) : embeddingModelValue(model);

    const emit = patch => {
        const next = {...current, ...patch};
        if (Object.prototype.hasOwnProperty.call(patch, 'provider') && patch.provider !== 'AzureOpenAI') {
            delete next.endpoint;
        }
        if (isEqual(next, current)) {
            return;
        }
        current = next;
        if (Object.prototype.hasOwnProperty.call(patch, 'model') && listingError) {
            listingError = '';
            syncModelOptions();
        }
        if (Object.keys(patch).some(name => ['credentials', 'provider', 'endpoint'].includes(name))) {
            listedModels = [];
            listingError = '';
            syncModelOptions(true);
            onRender(fields);
        }
        onChange(cloneValue(current));
    };

    let listedModels = [];
    let listingError = '';

    const credentialsField = createDropdown({
        label: mxResources.get('credentials'), id: 'embeddingModelCredentials', name: 'embeddingModel.credentials',
        value: current.credentials, searchable: true, clearable: true, className: 'required', error: errors?.credentials,
        options: [{value: '', name: mxResources.get('credentialsSelect')},
            ...(current.credentials ? [{value: current.credentials, name: current.credentials}] : [])]
    });
    const credentials = credentialsField.querySelector('select');
    credentials.className += ' credentials';
    credentials.addEventListener('change', event => emit({credentials: event.target.value}));
    fields.appendChild(credentialsField);

    const providerField = createDropdown({
        label: mxResources.get('aiProvider'), id: 'embeddingModelProvider', name: 'embeddingModel.provider',
        value: current.provider, className: 'required', searchable: true, error: errors?.provider,
        help: mxResources.get('embeddingProviderHelp'),
        options: [{value: '', name: mxResources.get('aiProviderSelect')},
            ...embeddingProviders.map(provider => ({value: provider, name: provider}))]
    });
    const provider = providerField.querySelector('select');
    const providerHelp = providerField.children[providerField.children.length - 1];
    provider.addEventListener('change', event => {
        emit({provider: event.target.value});
        endpointField.hidden = event.target.value !== 'AzureOpenAI';
    });
    fields.appendChild(providerField);

    const modelField = createDropdown({
        label: mxResources.get('model'), id: 'embeddingModelModel', name: 'embeddingModel.model',
        value: current.model, className: 'required', error: errors?.model,
        searchable: true, allowAdditions: true, help: mxResources.get('embeddingModelSelectionHelp'),
        options: [{value: '', name: mxResources.get('modelSelect')},
            ...(current.model ? [{value: current.model, name: current.model}] : [])]
    });
    const modelDropdown = modelField.querySelector('select');
    modelDropdown.className += ' embedding-model-dropdown';
    modelDropdown.addEventListener('change', event => emit({model: event.target.value}));
    modelDropdown.addEventListener('embedding-models-listed', event => {
        listedModels = Array.isArray(event.detail?.models) ? event.detail.models : [];
        listingError = '';
        if (event.detail?.render !== false) {
            syncModelOptions();
        }
    });
    modelDropdown.addEventListener('embedding-models-listing-error', event => {
        listedModels = [];
        listingError = current.model ? '' : event.detail?.message || mxResources.get('embeddingModelListingFailed');
        if (event.detail?.render !== false) {
            syncModelOptions();
        }
    });
    fields.appendChild(modelField);

    function syncModelOptions(sourceChanged = false) {
        const selected = current.model;
        modelDropdown.innerHTML = '';
        const options = new Map();
        if (selected) {
            options.set(selected, selected);
        }
        listedModels.forEach(model => {
            const id = String(model?.id || '').trim();
            if (id) {
                options.set(id, String(model?.label || id));
            }
        });
        [{value: '', name: mxResources.get('modelSelect')},
            ...[...options].map(([value, name]) => ({value, name}))].forEach(option => {
            const element = document.createElement('option');
            element.value = option.value;
            element.textContent = option.name;
            if (option.value === selected) {
                element.selected = true;
            }
            modelDropdown.appendChild(element);
        });
        modelDropdown.value = selected;
        modelDropdown.dataset.embeddingModelCredentials = current.credentials;
        modelDropdown.dataset.embeddingModelProvider = listedProviders.has(current.provider)
            ? current.provider : '';
        if (sourceChanged) {
            delete modelDropdown.dataset.embeddingModelSuggestionsLoaded;
            delete modelDropdown.dataset.embeddingModelDropdownInitialized;
        }
        if (modelDropdown.dataset.embeddingModelDropdownInitialized === 'true' && typeof $ === 'function') {
            $(modelDropdown).dropdown('refresh');
            $(modelDropdown).dropdown('set selected', selected);
        }
        [...modelField.children].filter(child => child.className?.includes('embedding-model-listing-error'))
            .forEach(child => child.remove());
        if (listingError) {
            appendFieldError(modelField, listingError, {className: 'embedding-model-listing-error'});
        }
    }

    const dimensionsField = createInputField({
        label: mxResources.get('embeddingDimensions'), id: 'embeddingModelDimensions', name: 'embeddingModel.dimensions',
        type: 'number', min: 1, value: stringValue(current.dimensions), className: 'required',
        error: errors?.dimensions, help: mxResources.get('embeddingDimensionsHelp')
    });
    dimensionsField.querySelector('input').addEventListener('change', event =>
        emit({dimensions: event.target.value === '' ? null : Number(event.target.value)}));
    const dimensionsHelp = dimensionsField.children[dimensionsField.children.length - 1];
    fields.appendChild(dimensionsField);

    const endpointField = createInputField({
        label: mxResources.get('providerEndpoint'), id: 'embeddingModelEndpoint', name: 'embeddingModel.endpoint',
        type: 'url', value: current.endpoint || '', error: errors?.endpoint
    });
    endpointField.hidden = current.provider !== 'AzureOpenAI';
    endpointField.querySelector('input').addEventListener('change', event => emit({endpoint: event.target.value}));
    fields.appendChild(endpointField);

    const setManaged = enabled => {
        managed = enabled;
        current = enabled ? managedEmbeddingModelValue(current) : embeddingModelValue(current);
        provider.value = current.provider;
        modelDropdown.value = current.model;
        dimensionsField.querySelector('input').value = stringValue(current.dimensions);
        endpointField.querySelector('input').value = current.endpoint || '';
        dimensionsField.hidden = enabled;
        dimensionsField.querySelector('input').disabled = enabled;
        providerHelp.style.display = '';
        dimensionsHelp.style.display = '';
        endpointField.hidden = current.provider !== 'AzureOpenAI';
        syncModelOptions(true);
        return cloneValue(current);
    };
    setManaged(managed);

    return {element: fields, getValue: () => cloneValue(current), setManaged};
};

export {createEmbeddingModelFields, embeddingModelValue, managedEmbeddingModelValue};
